import React from "react";
import { Skeleton, Box, Stack, Card, CardContent } from "@mui/material";
import { useTheme } from "@mui/system";

const AutobiaSkeleton = ({
  variant = "rectangular",
  width = "100%",
  height = 20,
  animation = "pulse",
  sx = {},
}) => {
  const theme = useTheme();

  return (
    <Skeleton
      variant={variant}
      width={width}
      height={height}
      animation={animation}
      sx={{
        backgroundColor: theme.palette.gray.g100,
        borderRadius: variant === "rectangular" ? "4px" : undefined,
        ...sx,
      }}
    />
  );
};

// Text Skeleton Patterns
export const AutobiaTextSkeleton = ({
  lines = 3,
  spacing = 1,
  lastLineWidth = "60%",
  sx = {},
}) => {
  return (
    <Stack spacing={spacing} sx={sx}>
      {Array.from({ length: lines }).map((_, index) => (
        <AutobiaSkeleton
          key={index}
          height={16}
          width={index === lines - 1 ? lastLineWidth : "100%"}
        />
      ))}
    </Stack>
  );
};

// Card Skeleton
export const AutobiaCardSkeleton = ({
  hasAvatar = false,
  hasImage = false,
  imageHeight = 200,
  textLines = 3,
  hasActions = false,
  sx = {},
}) => {
  const theme = useTheme();

  return (
    <Card
      sx={{
        borderRadius: "12px",
        border: `1px solid ${theme.palette.gray.g200}`,
        ...sx,
      }}
    >
      {/* Image Skeleton */}
      {hasImage && (
        <AutobiaSkeleton
          variant="rectangular"
          width="100%"
          height={imageHeight}
          sx={{ borderRadius: 0 }}
        />
      )}

      <CardContent sx={{ p: 3 }}>
        {/* Header with Avatar */}
        {hasAvatar && (
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <AutobiaSkeleton
              variant="circular"
              width={40}
              height={40}
            />
            <Stack spacing={1} sx={{ flex: 1 }}>
              <AutobiaSkeleton width="40%" height={16} />
              <AutobiaSkeleton width="60%" height={14} />
            </Stack>
          </Stack>
        )}

        {/* Content */}
        <Stack spacing={1}>
          <AutobiaSkeleton width="80%" height={20} />
          <AutobiaTextSkeleton lines={textLines} />
        </Stack>

        {/* Actions */}
        {hasActions && (
          <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
            <AutobiaSkeleton width={80} height={36} />
            <AutobiaSkeleton width={80} height={36} />
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

// Table Row Skeleton
export const AutobiaTableSkeleton = ({
  rows = 5,
  columns = 4,
  sx = {},
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.gray.g200}`,
        borderRadius: "8px",
        overflow: "hidden",
        ...sx,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          p: 2,
          backgroundColor: theme.palette.gray.g50,
          borderBottom: `1px solid ${theme.palette.gray.g200}`,
        }}
      >
        {Array.from({ length: columns }).map((_, index) => (
          <Box key={index} sx={{ flex: 1, mr: index < columns - 1 ? 2 : 0 }}>
            <AutobiaSkeleton width="70%" height={16} />
          </Box>
        ))}
      </Box>

      {/* Rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <Box
          key={rowIndex}
          sx={{
            display: "flex",
            p: 2,
            borderBottom: rowIndex < rows - 1 ? `1px solid ${theme.palette.gray.g100}` : "none",
          }}
        >
          {Array.from({ length: columns }).map((_, colIndex) => (
            <Box key={colIndex} sx={{ flex: 1, mr: colIndex < columns - 1 ? 2 : 0 }}>
              <AutobiaSkeleton
                width={colIndex === 0 ? "90%" : "60%"}
                height={16}
              />
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
};

// List Item Skeleton
export const AutobiaListSkeleton = ({
  items = 5,
  hasAvatar = true,
  hasIcon = false,
  sx = {},
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.gray.g200}`,
        borderRadius: "8px",
        overflow: "hidden",
        ...sx,
      }}
    >
      {Array.from({ length: items }).map((_, index) => (
        <Box key={index}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              p: 2,
              gap: 2,
            }}
          >
            {/* Avatar or Icon */}
            {hasAvatar && (
              <AutobiaSkeleton
                variant="circular"
                width={40}
                height={40}
              />
            )}
            {hasIcon && !hasAvatar && (
              <AutobiaSkeleton
                variant="rectangular"
                width={24}
                height={24}
              />
            )}

            {/* Content */}
            <Stack spacing={1} sx={{ flex: 1 }}>
              <AutobiaSkeleton width="70%" height={16} />
              <AutobiaSkeleton width="50%" height={14} />
            </Stack>

            {/* Trailing */}
            <AutobiaSkeleton width={60} height={14} />
          </Box>
          
          {index < items - 1 && (
            <Box sx={{ height: 1, backgroundColor: theme.palette.gray.g100, mx: 2 }} />
          )}
        </Box>
      ))}
    </Box>
  );
};

// Page Skeleton (Complete page loading)
export const AutobiaPageSkeleton = ({ sx = {} }) => {
  return (
    <Box sx={{ p: 3, ...sx }}>
      {/* Header */}
      <Stack spacing={2} sx={{ mb: 6 }}>
        <AutobiaSkeleton width="300px" height={32} />
        <AutobiaSkeleton width="500px" height={20} />
        <Stack direction="row" spacing={1}>
          <AutobiaSkeleton width={80} height={24} />
          <AutobiaSkeleton width={100} height={24} />
          <AutobiaSkeleton width={90} height={24} />
        </Stack>
      </Stack>

      {/* Content Grid */}
      <Stack spacing={4}>
        {/* Cards Row */}
        <Stack direction="row" spacing={2}>
          {Array.from({ length: 4 }).map((_, index) => (
            <Box key={index} sx={{ flex: 1 }}>
              <AutobiaCardSkeleton textLines={1} />
            </Box>
          ))}
        </Stack>

        {/* Table Section */}
        <Stack spacing={2}>
          <AutobiaSkeleton width="200px" height={24} />
          <AutobiaTableSkeleton rows={8} columns={5} />
        </Stack>
      </Stack>
    </Box>
  );
};

export default AutobiaSkeleton;